import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { CheckCircle, Compass, User, ArrowRight, Sparkles, FileText, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import CharterAcceptanceModal from '@/components/CharterAcceptanceModal';

const WelcomePage = () => {
  const { t, i18n } = useTranslation();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [showCharterModal, setShowCharterModal] = useState(false);

  const isExplorer = user?.role === 'explorer';
  const hasAcceptedCharter = user?.charter_accepted === true;

  // Pour les guides, vérifier si la charte est acceptée
  useEffect(() => {
    if (isExplorer && !hasAcceptedCharter) {
      // Afficher automatiquement le modal de charte pour les nouveaux guides
      setShowCharterModal(true);
    }
  }, [isExplorer, hasAcceptedCharter]);

  const handleCharterAccepted = () => {
    setShowCharterModal(false);
    refreshUser();
  };

  return (
    <main className="min-h-screen pt-20 pb-12 flex items-center justify-center bg-gradient-to-b from-orange-50 to-white" data-testid="welcome-page">
      <div className="max-w-2xl mx-auto px-4 text-center">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 200, damping: 15 }}
          className="mb-8"
        >
          <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h1 className="text-3xl md:text-4xl font-bold mb-4">
            <span className="flex items-center justify-center gap-2">
              <Sparkles className="w-8 h-8 text-primary" />
              Bienvenue {user?.name} !
              <Sparkles className="w-8 h-8 text-primary" />
            </span>
          </h1>
          
          <p className="text-xl text-muted-foreground mb-8">
            {isExplorer 
              ? "Votre compte guide est créé ! Vous êtes prêt(e) à partager vos expériences uniques."
              : "Votre inscription est confirmée ! Vous êtes prêt(e) à vivre des XPériences inoubliables."
            }
          </p>
        </motion.div>

        {/* Pour les guides : Afficher l'étape obligatoire de la charte si pas encore acceptée */}
        {isExplorer && !hasAcceptedCharter && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-6 mb-8"
          >
            <div className="flex items-center justify-center gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
              <h2 className="text-lg font-bold text-amber-800">
                {i18n.language === 'fr' ? 'Étape obligatoire' : 'Required step'}
              </h2>
            </div>
            <p className="text-amber-700 mb-4">
              {i18n.language === 'fr' 
                ? 'Avant de créer votre première expérience, vous devez accepter la Charte des Guides et les Conditions Générales d\'Utilisation.'
                : 'Before creating your first experience, you must accept the Guide Charter and Terms of Service.'}
            </p>
            <Button 
              onClick={() => setShowCharterModal(true)}
              className="bg-amber-600 hover:bg-amber-700"
              size="lg"
            >
              <FileText className="w-5 h-5 mr-2" />
              {i18n.language === 'fr' ? 'Lire et accepter la Charte' : 'Read and accept the Charter'}
            </Button>
          </motion.div>
        )}

        {/* Options après acceptation de la charte (ou pour les voyageurs) */}
        {(!isExplorer || hasAcceptedCharter) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-2xl shadow-lg p-8 mb-8"
          >
            <h2 className="text-lg font-semibold mb-6">
              {isExplorer ? "Que souhaitez-vous faire ?" : "Par où commencer ?"}
            </h2>
            
            <div className="grid gap-4">
              {isExplorer ? (
                <>
                  <Link to="/dashboard?tab=experiences&action=create">
                    <Button className="w-full h-14 text-lg" size="lg">
                      <Compass className="w-5 h-5 mr-2" />
                      Créer ma première expérience
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/dashboard">
                    <Button variant="outline" className="w-full h-12" size="lg">
                      <User className="w-5 h-5 mr-2" />
                      Compléter mon profil
                    </Button>
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/explore">
                  <Button className="w-full h-14 text-lg" size="lg">
                    <Compass className="w-5 h-5 mr-2" />
                    Explorer les expériences
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/dashboard">
                  <Button variant="outline" className="w-full h-12" size="lg">
                    <User className="w-5 h-5 mr-2" />
                    Voir mon profil
                  </Button>
                </Link>
              </>
            )}
          </div>
        </motion.div>
        )}

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="text-sm text-muted-foreground"
        >
          Besoin d'aide ? <Link to="/help" className="text-primary hover:underline">Consultez notre guide</Link>
        </motion.p>
      </div>

      {/* Modal de Charte obligatoire pour les guides */}
      {isExplorer && (
        <CharterAcceptanceModal
          isOpen={showCharterModal}
          onClose={() => {}} 
          onAccepted={handleCharterAccepted}
          user={user}
          canClose={false}
        />
      )}
    </main>
  );
};

export default WelcomePage;
